import { useCallback, useEffect, useMemo, useState } from 'react'
import { useExperimentService, useUserService } from '../services'
import {
  UrlBasedUsageTracker,
  isToolsFreeUseLimitToTrialTestTreatment,
  toolsFreeUseLimitToTrialLimits,
} from '@lib/utils/tool-usage'
import { TrialModalContent } from '@components/ui-lib/modal/free-user-trial-modal'

interface UseFreeLimitToTrialExperiment {
  usageTracker: UrlBasedUsageTracker | undefined
  showTrialModal: boolean
  setShowTrialModal: (show: boolean) => void
  isToolsFreeUseLimitExperiment: boolean
  trialModalContent: TrialModalContent
  trackModalShow?: () => void
}

export const useFreeLimitToTrialExperiment = (): UseFreeLimitToTrialExperiment => {
  const { currentUser } = useUserService()
  const { getToolsFreeUseLimitToTrialAssignment } = useExperimentService()
  const [toolsFreeUseLimitAssignment] = useState(() => getToolsFreeUseLimitToTrialAssignment())
  const [showTrialModal, setShowTrialModal] = useState(false)

  const isToolsFreeUseLimitExperiment = isToolsFreeUseLimitToTrialTestTreatment(toolsFreeUseLimitAssignment)

  const handleLimitReached = useCallback(() => {
    setShowTrialModal(true)
  }, [])

  const usageTracker = useMemo(() => {
    if (isToolsFreeUseLimitExperiment) {
      return new UrlBasedUsageTracker(
        toolsFreeUseLimitToTrialLimits[toolsFreeUseLimitAssignment],
        false,
        undefined,
        currentUser
      )
    }
    return undefined
  }, [isToolsFreeUseLimitExperiment, toolsFreeUseLimitAssignment, currentUser])

  // Add and clean up event listener
  useEffect(() => {
    if (usageTracker) {
      usageTracker.addEventListener('limitreached', handleLimitReached)
      return () => {
        usageTracker.removeEventListener('limitreached', handleLimitReached)
      }
    }
  }, [usageTracker, handleLimitReached])

  return {
    usageTracker,
    showTrialModal,
    setShowTrialModal,
    isToolsFreeUseLimitExperiment,
    trialModalContent: {
      ctaText: 'Try Grammarly Pro for free',
      description: "You've reached your daily free limit. Start your free trial today to get full access to Grammarly.",
      showDisclaimer: true,
    },
  }
}
